/**
 * Modal de confirmación para cierre involuntario de cuenta.
 *
 * @taskId T464
 * @see functional/Specs/specs_cycle_4.md HU-15-FE
 */

import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, X } from 'lucide-react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from '@flexibilitypay/ui';

import { useCloseAccountInvoluntary } from '../hooks/use-closure';
import type { InvoluntaryClosureReason } from '../types/closure.types';
import { INVOLUNTARY_REASON_LABELS } from '../types/closure.types';

export interface CloseAccountModalProps {
  /** ID de la cuenta a cerrar */
  accountId: string;
  /** Nombre/razón social del titular (para mostrar en el modal) */
  accountHolder?: string;
  /** CVU de la cuenta (para mostrar en el modal) */
  cvu?: string;
  /** Si el modal está abierto */
  open: boolean;
  /** Callback cuando el modal se cierra */
  onOpenChange: (open: boolean) => void;
  /** Callback tras éxito — redirección a estado de cierre (HU-16-FE) */
  onSuccess: (closureId: string) => void;
}

type ConfirmationStep = 'form' | 'confirm';

/**
 * Modal para iniciar cierre involuntario de una cuenta.
 *
 * Flujo:
 * 1. Usuario selecciona motivo (OPERATIVO/REGULATORIO/RIESGO)
 * 2. Clic en "Iniciar cierre" → Modal de confirmación (P-09)
 * 3. Confirmar → POST al backend → feedback + redirect
 *
 * @rule RULE-29 — Solo PSP_ADMIN puede usar (verificado por parent)
 * @rule RULE-16 — Motivo obligatorio
 * @rule P-09 — Confirmación explícita antes de acción crítica
 */
export function CloseAccountModal({
  accountId,
  accountHolder,
  cvu,
  open,
  onOpenChange,
  onSuccess,
}: CloseAccountModalProps) {
  const { t } = useTranslation(['closure', 'common']);
  const [step, setStep] = useState<ConfirmationStep>('form');
  const [selectedReason, setSelectedReason] = useState<InvoluntaryClosureReason | ''>('');
  const [observaciones, setObservaciones] = useState('');

  const closeAccountMutation = useCloseAccountInvoluntary();

  // ─── Handlers ─────────────────────────────────────────────────────────────

  const handleReasonChange = useCallback((value: string) => {
    setSelectedReason(value as InvoluntaryClosureReason);
  }, []);

  const handleObservacionesChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setObservaciones(e.target.value);
    },
    [],
  );

  const handleInitiateClose = useCallback(() => {
    if (!selectedReason) return;
    setStep('confirm');
  }, [selectedReason]);

  const handleCancel = useCallback(() => {
    // Caso 5: Cancelar en modal — sin acción al backend
    setStep('form');
  }, []);

  const handleConfirm = useCallback(async () => {
    if (!selectedReason) return;

    try {
      const result = await closeAccountMutation.mutateAsync({
        accountId,
        payload: {
          motivo: selectedReason,
          observaciones: observaciones.trim() || undefined,
        },
      });

      // Caso 6: Éxito → callback para redirect a HU-16-FE
      onSuccess(result.closureId);
      onOpenChange(false);

      // Reset state
      setStep('form');
      setSelectedReason('');
      setObservaciones('');
    } catch {
      // Error se maneja por la mutation — se muestra en UI
      setStep('form');
    }
  }, [accountId, selectedReason, observaciones, closeAccountMutation, onSuccess, onOpenChange]);

  const handleClose = useCallback(() => {
    if (closeAccountMutation.isPending) return; // No cerrar durante submit
    onOpenChange(false);
    setStep('form');
    setSelectedReason('');
    setObservaciones('');
  }, [onOpenChange, closeAccountMutation.isPending]);

  // ─── Render ───────────────────────────────────────────────────────────────

  const isFormValid = selectedReason !== '';

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[425px]">
        {step === 'form' ? (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-destructive" />
                {t('closure:modal.title', 'Cierre involuntario de cuenta')}
              </DialogTitle>
              <DialogDescription>
                {t(
                  'closure:modal.description',
                  'Esta acción iniciará el proceso de cierre involuntario. El proceso incluye la restitución de fondos si corresponde.',
                )}
              </DialogDescription>
            </DialogHeader>

            {/* Info de la cuenta */}
            {(accountHolder || cvu) && (
              <div className="rounded-md border bg-muted/50 p-3 text-sm">
                {accountHolder && (
                  <p>
                    <span className="font-medium">{t('closure:modal.holder', 'Titular:')} </span>
                    {accountHolder}
                  </p>
                )}
                {cvu && (
                  <p>
                    <span className="font-medium">{t('closure:modal.cvu', 'CVU:')} </span>
                    <code className="text-xs">{cvu}</code>
                  </p>
                )}
              </div>
            )}

            {/* Formulario */}
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="motivo">
                  {t('closure:modal.reasonLabel', 'Motivo del cierre')}
                  <span className="text-destructive"> *</span>
                </Label>
                <Select value={selectedReason} onValueChange={handleReasonChange}>
                  <SelectTrigger id="motivo">
                    <SelectValue
                      placeholder={t('closure:modal.reasonPlaceholder', 'Seleccione un motivo')}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {(Object.keys(INVOLUNTARY_REASON_LABELS) as InvoluntaryClosureReason[]).map(
                      (reason) => (
                        <SelectItem key={reason} value={reason}>
                          {INVOLUNTARY_REASON_LABELS[reason]}
                        </SelectItem>
                      ),
                    )}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid gap-2">
                <Label htmlFor="observaciones">
                  {t('closure:modal.observationsLabel', 'Observaciones (opcional)')}
                </Label>
                <Textarea
                  id="observaciones"
                  placeholder={t(
                    'closure:modal.observationsPlaceholder',
                    'Detalle adicional sobre el cierre...',
                  )}
                  value={observaciones}
                  onChange={handleObservacionesChange}
                  maxLength={500}
                  rows={3}
                />
              </div>
            </div>

            {/* Error de la mutation */}
            {closeAccountMutation.isError && (
              <div className="rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
                {closeAccountMutation.error?.message ||
                  t('closure:modal.genericError', 'Error al iniciar el cierre')}
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={handleClose}>
                {t('common:cancel', 'Cancelar')}
              </Button>
              <Button
                variant="destructive"
                onClick={handleInitiateClose}
                disabled={!isFormValid || closeAccountMutation.isPending}
              >
                {t('closure:modal.initiateButton', 'Iniciar cierre')}
              </Button>
            </DialogFooter>
          </>
        ) : (
          /* Step: Confirmación (P-09) */
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-destructive">
                <AlertTriangle className="h-5 w-5" />
                {t('closure:confirm.title', '¿Confirmar cierre involuntario?')}
              </DialogTitle>
              <DialogDescription>
                {t(
                  'closure:confirm.description',
                  'Esta acción iniciará el cierre involuntario de la cuenta. El proceso es irreversible una vez completado.',
                )}
              </DialogDescription>
            </DialogHeader>

            <div className="rounded-md border border-destructive/30 bg-destructive/5 p-4">
              <p className="text-sm">
                <strong>{t('closure:confirm.reasonLabel', 'Motivo seleccionado:')}</strong>{' '}
                {selectedReason && INVOLUNTARY_REASON_LABELS[selectedReason]}
              </p>
              {observaciones && (
                <p className="mt-2 text-sm text-muted-foreground">
                  <strong>{t('closure:confirm.observations', 'Observaciones:')}</strong>{' '}
                  {observaciones}
                </p>
              )}
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={handleCancel}
                disabled={closeAccountMutation.isPending}
              >
                {t('common:back', 'Volver')}
              </Button>
              <Button
                variant="destructive"
                onClick={handleConfirm}
                disabled={closeAccountMutation.isPending}
              >
                {closeAccountMutation.isPending
                  ? t('closure:confirm.processing', 'Procesando...')
                  : t('closure:confirm.confirmButton', 'Confirmar cierre')}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
